import * as React from "react";
import { cn } from "../lib/utils.js";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /**
   * Grow the textarea to fit its content instead of scrolling inside a
   * fixed box (Telegram-style composer behavior). Height is recomputed on
   * every value change; pair with a `max-h-*` + `overflow-y-auto` in
   * `className` to cap the growth and scroll past it. Off by default so
   * every other textarea (form fields, dialogs) keeps its fixed height.
   */
  autoGrow?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, autoGrow, onChange, ...props }, ref) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);

    // Merge the forwarded ref with our internal one so callers keep their
    // DOM handle (focus, selection) while autoGrow measures the element.
    const setRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref)
          (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current =
            node;
      },
      [ref],
    );

    const resize = React.useCallback(() => {
      const el = innerRef.current;
      if (!el || !autoGrow) return;
      // Collapse to natural height first so shrinking (deleting lines)
      // measures correctly; the CSS min-h floors it, max-h + overflow cap it.
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }, [autoGrow]);

    // Re-fit before paint whenever the controlled value changes (and once
    // on mount) so there is no flash of the un-sized box.
    React.useLayoutEffect(() => {
      resize();
    }, [resize, props.value]);

    return (
      <textarea
        className={cn(
          "flex min-h-[80px] w-full min-w-0 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/25 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:border-destructive aria-[invalid=true]:focus-visible:ring-destructive/30",
          className,
        )}
        ref={setRefs}
        onChange={(e) => {
          onChange?.(e);
          resize();
        }}
        {...props}
      />
    );
  },
);
Textarea.displayName = "Textarea";

export { Textarea };
